/**
 * API calls that proxy through Vercel serverless functions
 * API keys are kept server-side — never exposed in the browser
 */

import { SupportedLanguage, TranslationSource, WordType, NounInfo, VerbInfo, AdjectiveInfo, UsageExample } from "@/types";
import { auth } from "@/lib/firebase";

async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

// ===========================================
// Translation
// ===========================================

export async function translateText(
  text: string,
  sourceLang: SupportedLanguage,
  targetLang: SupportedLanguage,
  mode: "google" | "openai" = "google",
  speakerGender?: "male" | "female",
  addresseeGender?: "male" | "female",
  addresseeNumber?: "single" | "plural"
): Promise<{ translatedText: string; source: TranslationSource; transliteration?: string }> {
  const headers = await getAuthHeaders();
  const response = await fetch("/api/translate", {
    method: "POST",
    headers,
    body: JSON.stringify({
      text,
      sourceLang,
      targetLang,
      mode: mode === "openai" ? "llm" : "google",
      speakerGender,
      addresseeGender,
      addresseeNumber,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Translation failed");
  }

  const data = await response.json();
  return {
    translatedText: data.translatedText,
    source: data.source as TranslationSource,
    transliteration: data.transliteration,
  };
}

// ===========================================
// Parse Sentence
// ===========================================

export interface ParsedWord {
  original: string;
  normalized: string;
  translation: string;
  position: number;
}

export interface ParseSentenceResult {
  fullTranslation: string;
  words: ParsedWord[];
}

export async function parseSentence(
  text: string,
  sourceLang: string = "he",
  targetLang: string = "en"
): Promise<ParseSentenceResult> {
  const headers = await getAuthHeaders();
  const response = await fetch("/api/parse-sentence", {
    method: "POST",
    headers,
    body: JSON.stringify({ text, sourceLang, targetLang }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to parse sentence");
  }

  return await response.json();
}

// ===========================================
// Categorization
// ===========================================

export interface GeneratedWord {
  hebrew: string;
  english: string;
  category?: string;
}

export async function generateVocabularyWords(
  topic: string,
  description: string,
  count: number = 10,
  onboarding: boolean = false,
  existingCategories: string[] = []
): Promise<GeneratedWord[]> {
  const headers = await getAuthHeaders();
  const body: Record<string, unknown> = { topic, description, count, onboarding };
  if (existingCategories.length > 0) {
    body.existingCategories = existingCategories;
  }
  const response = await fetch("/api/vocabulary", {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to generate vocabulary");
  }

  const data = await response.json();
  return data.words.map((w: any) => ({
    hebrew: w.hebrew,
    english: w.english,
    category: w.category || "Uncategorized",
  }));
}

// ===========================================
// Import Extraction
// ===========================================

export interface ExtractedWord {
  hebrew: string;
  english?: string;
}

/**
 * Extract Hebrew vocabulary pairs from messy text (PDF dumps, unstructured
 * lists) via the LLM. Used by the import feature when client-side parsing
 * can't find a clean structure.
 */
export async function extractWordsFromText(text: string): Promise<ExtractedWord[]> {
  const headers = await getAuthHeaders();
  const response = await fetch("/api/import-extract", {
    method: "POST",
    headers,
    body: JSON.stringify({ text }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to extract words from file");
  }

  const data = await response.json();
  return data.words;
}

// ===========================================
// Audio (Text-to-Speech)
// ===========================================

export async function getAudioForWord(hebrewText: string): Promise<string> {
  const headers = await getAuthHeaders();
  const response = await fetch("/api/tts", {
    method: "POST",
    headers,
    body: JSON.stringify({ text: hebrewText, language: "he" }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Text-to-speech failed");
  }

  const data = await response.json();
  return data.audioContent; // base64 MP3
}

// ===========================================
// Grammar Analysis
// ===========================================

export interface GrammarAnalysis {
  wordType: WordType;
  nounInfo?: NounInfo;
  verbInfo?: VerbInfo;
  adjectiveInfo?: AdjectiveInfo;
}

export async function analyzeWordGrammar(hebrewText: string): Promise<GrammarAnalysis> {
  const headers = await getAuthHeaders();
  const response = await fetch("/api/grammar", {
    method: "POST",
    headers,
    body: JSON.stringify({ hebrewText }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Grammar analysis failed");
  }

  const parsed = await response.json();

  // Validate wordType
  const validWordTypes: WordType[] = ["noun", "verb", "adjective", "adverb", "other"];
  if (!validWordTypes.includes(parsed.wordType)) {
    parsed.wordType = "other";
  }

  return {
    wordType: parsed.wordType,
    nounInfo: parsed.nounInfo,
    verbInfo: parsed.verbInfo,
    adjectiveInfo: parsed.adjectiveInfo,
  };
}

// ===========================================
// Example Sentences
// ===========================================

export async function generateExampleSentences(
  hebrewWord: string,
  translation?: string,
  wordType?: WordType
): Promise<UsageExample[]> {
  const headers = await getAuthHeaders();
  const response = await fetch("/api/examples", {
    method: "POST",
    headers,
    body: JSON.stringify({ hebrewWord, translation, wordType }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Example generation failed");
  }

  const data = await response.json();
  return data.examples as UsageExample[];
}

// ===========================================
// Categorization
// ===========================================

export async function categorizeWord(
  hebrewText: string,
  existingCategories: string[] = [],
  userContext: string = "",
  translation: string = ""
): Promise<string[]> {
  try {
    const headers = await getAuthHeaders();
    const body: Record<string, unknown> = { hebrewText };
    if (existingCategories.length > 0) {
      body.existingCategories = existingCategories;
    }
    if (userContext) {
      body.userContext = userContext;
    }
    if (translation) {
      body.translation = translation;
    }
    const response = await fetch("/api/categorize", {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      return ["Uncategorized"];
    }

    const data = await response.json();
    if (Array.isArray(data.categories) && data.categories.length > 0) {
      return data.categories;
    }

    return ["Uncategorized"];
  } catch (error) {
    console.error("Categorization error:", error);
    return ["Uncategorized"];
  }
}
